import type { Promotion, SignupBonusOffers, SignupBonusState } from '@promotion/types'
import { computed, navigateTo, toValue, useApi, useCoreConfig, useDayjs, useMutation, useQuery, useQueryClient, useRewardChecks, useI18n } from '#imports'
import type { UseMutationMutateOptions, UseQueryOverrideOptions } from '@core/types'

/**
 * Composable for fetching and managing active and enqueued rewards.
 * Handles API calls, state, and event-driven refresh for rewards.
 * @returns State and methods for rewards fetching and management
 */
export function useRewardsClient() {
    const queryClient = useQueryClient()
    const api = useApi({ enableLegacySupport: true, service: 'monolith' })
    const dayjs = useDayjs()
    const { t } = useI18n()

    function useGetActiveRewards(options: UseQueryOverrideOptions = {}) {
        return useQuery(async () => {
            const response = await api('/rewards/active')

            return response.active.sort(
                (a: Promotion, b: Promotion) => dayjs(a.expires_at).diff(dayjs(b.expires_at))
            )
        }, {
            lazy: true,
            server: false,
            ...options,
            key: 'promotions:rewards:active',
        })
    }

    function useGetEnqueuedRewards(options: UseQueryOverrideOptions = {}) {
        return useQuery(async () => {
            const response = await api('/rewards/enqueued')

            return response.enqueued.sort(
                (a: Promotion, b: Promotion) => dayjs(a.expires_at).diff(b.expires_at)
            )
        }, {
            server: false,
            lazy: true,
            ...options,
            key: 'promotions:rewards:enqueued'
        })
    }

    function useCancelReward(options: UseMutationMutateOptions = {}) {
        return useMutation(async (reward: Promotion) => {
            if (!reward) return

            const endpoint = reward?.cancel_url ?? '/rewards/cancel'
            return api(endpoint, {
                method: reward?.cancel_url ? 'PUT' : 'POST',
                body: {
                    promotionClaimId: reward.promotionClaimId,
                    bonusOfferId: reward.bonusOfferId
                }
            })
        }, {
            mode: 'leading',
            ...options,
            async onSuccess(...args) {
                if (options.onSuccess) {
                    await options.onSuccess(...args)
                }

                await queryClient.invalidateQueries({
                    key: 'promotions'
                })
            }
        })
    }

    function useActivateReward(options: UseMutationMutateOptions = {}) {
        const { site } = useCoreConfig()
        const { isBundle, REQUIREMENT_TYPE_PAYMENT } = useRewardChecks()

        const mutation = useMutation(async (reward: Promotion) => {
            if (!reward) return

            if (isBundle(reward)) {
                return api('/rewards/activate_signup_promotions', {
                    query: {
                        bundleId: reward.id || reward.bundleId,
                        brand: site.brand
                    }
                })
            }

            const endpoint = reward?.activate_url ?? '/rewards/activate'

            return api(endpoint, {
                method: reward?.activate_url ? 'PUT' : 'POST',
                body: {
                    promotionClaimId: reward?.promotionClaimId
                }
            })
        }, {
            mode: 'leading',
            ...options,
            async onSuccess(data, body) {
                if (options.onSuccess) {
                    await options.onSuccess(data, body)
                }

                // Redirect to payment after activation if response includes hasPaymentRequirement
                if (data?.hasPaymentRequirement) {
                    await navigateTo(t('pages.deposit'))
                    return
                }

                // Redirect to payment after activation if the reward has a payment requirement
                if (body?.requirement_type === REQUIREMENT_TYPE_PAYMENT) {
                    await navigateTo(t('pages.deposit'))
                    return
                }

                await queryClient.invalidateQueries({
                    key: 'promotions'
                })
            }
        })

        return mutation
    }

    function useGetPendingOffersWithPaymentRequirements(options: UseQueryOverrideOptions & {
        hasBonus: (offers: SignupBonusOffers) => boolean
    }) {
        const { hasBonus, ...queryOptions } = options

        const query = useQuery<SignupBonusOffers>(
            () => api<SignupBonusOffers>('/rewards/get_pending_offers_with_payment_requirements', { method: 'GET' }),
            {
                server: false,
                lazy: true,
                ...queryOptions,
                key: 'promotions:account-completion:signup-bonus',
            }
        )

        const hasSignupBonus = computed<SignupBonusState>(() => {
            if (!toValue(options.enabled)) return false
            if (query.status.value === 'error') return false
            if (query.status.value !== 'success') return undefined

            const offers = query.data.value
            if (!offers?.promotions?.length) return false

            return hasBonus(offers)
        })

        return query.enrich({ hasSignupBonus })
    }

    return {
        useGetActiveRewards,
        useGetEnqueuedRewards,
        useCancelReward,
        useActivateReward,
        useGetPendingOffersWithPaymentRequirements
    }
}
